import { useState } from 'react'
import { Link } from 'react-router-dom'
import {
  Book, MessageCircle, Sparkles, Layers, FolderGit,
  Database, FileCode, CreditCard, Settings, ChevronDown,
  ChevronRight, Search, ExternalLink, Bot, Terminal,
  CheckCircle, AlertCircle, HelpCircle
} from 'lucide-react'

interface FAQItem {
  question: string
  answer: string
}

interface GuideSection {
  title: string
  icon: React.ReactNode
  description: string
  subsections?: { title: string; content: string }[]
}

const guideSections: GuideSection[] = [
  {
    title: 'Getting Started',
    icon: <Book className="w-5 h-5" />,
    description: 'Welcome to HalCode! This guide will help you make the most of our AI-powered coding platform.',
    subsections: [
      {
        title: 'Creating Your Account',
        content: 'Sign up using your email or Google account. No credit card required for the free tier.'
      },
      {
        title: 'Setting Up API Keys',
        content: 'Add your Anthropic API key for Claude or Google API key for Gemini in the Settings page to enable AI features.'
      },
      {
        title: 'Your First Conversation',
        content: 'Click "New Chat" to start a conversation. Ask coding questions, request code reviews, or get help with bugs.'
      }
    ]
  },
  {
    title: 'Chat Features',
    icon: <MessageCircle className="w-5 h-5" />,
    description: 'The core of HalCode - an intelligent coding assistant that understands your codebase.',
    subsections: [
      {
        title: 'Streaming Responses',
        content: 'AI responses stream in real-time for a natural reading experience. Each chunk appears with a smooth 20ms delay for optimal performance.'
      },
      {
        title: 'Code Syntax Highlighting',
        content: 'Code blocks are automatically syntax-highlighted with Prism.js, supporting TypeScript, JavaScript, Python, SQL, Bash, and more.'
      },
      {
        title: 'Context Modes',
        content: 'Choose between Standard (code snippets), Git Repository (real file operations), or MCP (Model Context Protocol tools) modes.'
      },
      {
        title: 'Provider Selection',
        content: 'Switch between Claude (Anthropic) and Gemini (Google) providers based on your preference and API keys.'
      }
    ]
  },
  {
    title: 'Agentic Workflows',
    icon: <Sparkles className="w-5 h-5" />,
    description: 'Single-agent workflows that can read, write, and modify files in your codebase.',
    subsections: [
      {
        title: 'Enabling Agentic Mode',
        content: 'Switch to "Agentic" mode in the chat header to enable autonomous file operations.'
      },
      {
        title: 'Capabilities',
        content: 'Agentic mode can read files, create new files, modify existing code, run terminal commands, and search through your codebase.'
      },
      {
        title: 'Approval Process',
        content: 'Each file modification requires your approval before execution, ensuring full control over changes.'
      }
    ]
  },
  {
    title: 'Multi-Agent Workflows',
    icon: <Layers className="w-5 h-5" />,
    description: 'Three specialized AI agents working together: Architect, Developer, and QA Engineer.',
    subsections: [
      {
        title: 'How It Works',
        content: 'Describe what you want to build, and the Architect creates a plan, Developer implements it, and QA reviews the changes.'
      },
      {
        title: 'Architecture Planning',
        content: 'Review the generated architecture plan before approval. You can request modifications to the file structure.'
      },
      {
        title: 'Implementation Phases',
        content: 'Watch as agents work through tasks, with real-time progress tracking and detailed logs.'
      },
      {
        title: 'Final Review',
        content: 'Review all file changes before final approval. Reject or approve modifications as needed.'
      }
    ]
  },
  {
    title: 'Codebases',
    icon: <FolderGit className="w-5 h-5" />,
    description: 'Index and explore your code repositories for enhanced context-aware responses.',
    subsections: [
      {
        title: 'Creating a Codebase',
        content: 'Navigate to the Codebases page and create a new codebase by providing a repository URL or local path.'
      },
      {
        title: 'Indexing Status',
        content: 'Codebases are indexed for semantic search. Status indicators show when indexing is complete.'
      },
      {
        title: 'Semantic Search',
        content: 'Search across your indexed codebase using natural language queries to find relevant code quickly.'
      }
    ]
  },
  {
    title: 'Git Integration',
    icon: <Terminal className="w-5 h-5" />,
    description: 'Clone, manage, and work with Git repositories directly from HalCode.',
    subsections: [
      {
        title: 'Cloning Repositories',
        content: 'Enter a GitHub repository URL to clone it. Repositories are stored in a secure virtual file system.'
      },
      {
        title: 'File Operations',
        content: 'Read, edit, create, and delete files. All changes can be committed and pushed to GitHub.'
      },
      {
        title: 'Branch Management',
        content: 'Create branches, switch between them, and manage your Git workflow without leaving HalCode.'
      }
    ]
  },
  {
    title: 'MCP Servers',
    icon: <Database className="w-5 h-5" />,
    description: 'Model Context Protocol servers for extending HalCode capabilities.',
    subsections: [
      {
        title: 'What is MCP?',
        content: 'MCP (Model Context Protocol) allows AI assistants to connect with external tools and data sources.'
      },
      {
        title: 'Available Servers',
        content: 'Browse and connect to MCP servers for file systems, databases, APIs, and more.'
      },
      {
        title: 'Custom Servers',
        content: 'Add your own MCP servers by providing the server URL and configuration.'
      }
    ]
  },
  {
    title: 'Billing & Subscription',
    icon: <CreditCard className="w-5 h-5" />,
    description: 'Manage your subscription and billing settings.',
    subsections: [
      {
        title: 'Free Tier',
        content: 'Get started with our free tier - includes unlimited conversations and basic features.'
      },
      {
        title: 'Pro Tier',
        content: 'Upgrade for unlimited agentic workflows, larger context windows, and priority support.'
      },
      {
        title: 'Team Tier',
        content: 'Collaborate with your team with shared workspaces, team billing, and admin controls.'
      },
      {
        title: 'Managing Subscription',
        content: 'View current plan, upgrade, downgrade, or cancel from the Billing page.'
      }
    ]
  }
]

const faqs: FAQItem[] = [
  {
    question: 'How do I reset my password?',
    answer: 'Go to the login page and click "Forgot Password". Enter your email to receive a reset link.'
  },
  {
    question: 'Can I use HalCode offline?',
    answer: 'HalCode requires an internet connection for AI features. Some cached data may be available offline.'
  },
  {
    question: 'How is my data protected?',
    answer: 'All data is encrypted in transit and at rest. We never store your API keys on our servers.'
  },
  {
    question: 'Can I export my conversations?',
    answer: 'Yes! Click the ZIP button in the chat header to download all conversation files and code.'
  },
  {
    question: 'What languages does HalCode support?',
    answer: 'HalCode supports all major programming languages including TypeScript, JavaScript, Python, Go, Rust, and more.'
  },
  {
    question: 'How do I report a bug?',
    answer: 'Email support@halcode.com or use the feedback form in Settings > Help.'
  },
  {
    question: 'Can I integrate HalCode with my IDE?',
    answer: 'VS Code extension coming soon! For now, use the web interface or CLI tools.'
  },
  {
    question: 'How do I delete my account?',
    answer: 'Go to Settings > Account > Delete Account. This action is irreversible.'
  }
]

export default function HelpPage() {
  const [searchQuery, setSearchQuery] = useState('')
  const [expandedSection, setExpandedSection] = useState<string | null>('Getting Started')
  const [activeTab, setActiveTab] = useState<'guide' | 'faq'>('guide')

  const filteredSections = guideSections.filter(section =>
    section.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
    section.subsections?.some(sub =>
      sub.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      sub.content.toLowerCase().includes(searchQuery.toLowerCase())
    )
  )

  const filteredFaqs = faqs.filter(faq =>
    faq.question.toLowerCase().includes(searchQuery.toLowerCase()) ||
    faq.answer.toLowerCase().includes(searchQuery.toLowerCase())
  )

  const toggleSection = (title: string) => {
    setExpandedSection(expandedSection === title ? null : title)
  }

  return (
    <div className="h-full bg-dark-900 text-dark-200">
      {/* Header */}
      <div className="bg-dark-800/50 backdrop-blur-md border-b border-dark-700 p-6">
        <div className="max-w-4xl mx-auto">
          <div className="flex items-center gap-3 mb-4">
            <HelpCircle className="w-8 h-8 text-primary-400" />
            <h1 className="text-2xl font-bold text-white">Help & Documentation</h1>
          </div>
          <p className="text-dark-400 mb-4">
            Learn how to use HalCode effectively. Find guides, tutorials, and answers to common questions.
          </p>
          
          {/* Search */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-dark-400" />
            <input
              type="text"
              placeholder="Search documentation..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full bg-dark-700 border border-dark-600 rounded-lg pl-10 pr-4 py-3 text-dark-200 placeholder-dark-400 focus:outline-none focus:ring-2 focus:ring-primary-500"
            />
          </div>

          {/* Quick Links */}
          <div className="flex flex-wrap gap-2 mt-4">
            <Link to="/chat" className="flex items-center gap-2 px-3 py-1.5 bg-dark-700 hover:bg-dark-600 rounded-lg text-sm transition-colors">
              <MessageCircle className="w-4 h-4" />
              Start Chatting
            </Link>
            <Link to="/settings" className="flex items-center gap-2 px-3 py-1.5 bg-dark-700 hover:bg-dark-600 rounded-lg text-sm transition-colors">
              <Settings className="w-4 h-4" />
              Settings
            </Link>
            <Link to="/billing" className="flex items-center gap-2 px-3 py-1.5 bg-dark-700 hover:bg-dark-600 rounded-lg text-sm transition-colors">
              <CreditCard className="w-4 h-4" />
              Billing
            </Link>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="max-w-4xl mx-auto px-6 pt-4">
        <div className="flex gap-4 border-b border-dark-700">
          <button
            onClick={() => setActiveTab('guide')}
            className={`flex items-center gap-2 px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
              activeTab === 'guide'
                ? 'border-primary-500 text-primary-400'
                : 'border-transparent text-dark-400 hover:text-dark-200'
            }`}
          >
            <Book className="w-4 h-4" />
            User Guide
          </button>
          <button
            onClick={() => setActiveTab('faq')}
            className={`flex items-center gap-2 px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
              activeTab === 'faq'
                ? 'border-primary-500 text-primary-400'
                : 'border-transparent text-dark-400 hover:text-dark-200'
            }`}
          >
            <HelpCircle className="w-4 h-4" />
            FAQ
          </button>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-6 py-6">
        {activeTab === 'guide' ? (
          <div className="space-y-4">
            {filteredSections.length === 0 ? (
              <div className="text-center py-12">
                <Search className="w-12 h-12 text-dark-600 mx-auto mb-4" />
                <p className="text-dark-400">No results found for "{searchQuery}"</p>
              </div>
            ) : (
              filteredSections.map((section) => (
                <div
                  key={section.title}
                  className="bg-dark-800/50 backdrop-blur-sm border border-dark-700 rounded-xl overflow-hidden"
                >
                  <button
                    onClick={() => toggleSection(section.title)}
                    className="w-full flex items-center justify-between p-4 hover:bg-dark-700/50 transition-colors"
                  >
                    <div className="flex items-center gap-3">
                      <span className="text-primary-400">{section.icon}</span>
                      <span className="font-medium text-dark-200">{section.title}</span>
                    </div>
                    {expandedSection === section.title ? (
                      <ChevronDown className="w-5 h-5 text-dark-400" />
                    ) : (
                      <ChevronRight className="w-5 h-5 text-dark-400" />
                    )}
                  </button>

                  {expandedSection === section.title && section.subsections && (
                    <div className="border-t border-dark-700">
                      <p className="p-4 text-dark-400 text-sm">{section.description}</p>
                      <div className="space-y-1">
                        {section.subsections.map((sub, idx) => (
                          <div
                            key={idx}
                            className="px-4 py-3 hover:bg-dark-700/30 transition-colors"
                          >
                            <h4 className="text-sm font-medium text-dark-200 mb-1">
                              {sub.title}
                            </h4>
                            <p className="text-sm text-dark-400">{sub.content}</p>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              ))
            )}
          </div>
        ) : (
          <div className="space-y-4">
            {filteredFaqs.length === 0 ? (
              <div className="text-center py-12">
                <Search className="w-12 h-12 text-dark-600 mx-auto mb-4" />
                <p className="text-dark-400">No results found for "{searchQuery}"</p>
              </div>
            ) : (
              filteredFaqs.map((faq, idx) => (
                <div
                  key={idx}
                  className="bg-dark-800/50 backdrop-blur-sm border border-dark-700 rounded-xl p-4"
                >
                  <div className="flex items-start gap-3">
                    <AlertCircle className="w-5 h-5 text-primary-400 flex-shrink-0 mt-0.5" />
                    <div>
                      <h4 className="font-medium text-dark-200 mb-2">{faq.question}</h4>
                      <p className="text-sm text-dark-400">{faq.answer}</p>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        )}
      </div>

      {/* Contact Support */}
      <div className="max-w-4xl mx-auto px-6 py-8">
        <div className="bg-gradient-to-r from-primary-500/10 to-purple-500/10 border border-primary-500/20 rounded-xl p-6">
          <div className="flex items-start gap-4">
            <div className="w-10 h-10 rounded-lg bg-primary-500/20 flex items-center justify-center flex-shrink-0">
              <Bot className="w-5 h-5 text-primary-400" />
            </div>
            <div>
              <h3 className="font-semibold text-dark-200 mb-2">Still need help?</h3>
              <p className="text-dark-400 text-sm mb-4">
                Our support team is here to assist you with any questions or issues.
              </p>
              <div className="flex flex-wrap gap-3">
                <a
                  href="mailto:support@halcode.com"
                  className="flex items-center gap-2 px-3 py-1.5 bg-primary-500 hover:bg-primary-600 text-white rounded-lg text-sm transition-colors"
                >
                  <ExternalLink className="w-3 h-3" />
                  Email Support
                </a>
                <a
                  href="https://github.com/halcode"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-2 px-3 py-1.5 bg-dark-700 hover:bg-dark-600 text-dark-200 rounded-lg text-sm transition-colors"
                >
                  <ExternalLink className="w-3 h-3" />
                  GitHub
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
